import { Link, useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { getOrder } from '../api/ordersApi.js';
import { fmtTime } from '../utils/format.js';
import { LINE_ADD_URL } from '../constants/contact.js';
import './AccountOrderPages.css';

/**
 * 把 API（真實後端）與 mock 兩種訂單形狀正規化成頁面要的樣子：
 * - items：API 是物件陣列；mock 舊資料是字串 → 包成單一品項
 * - timeline：API 是 {status,label,createdAt} 歷程（最後一筆=目前）；
 *             mock 是 {done,label,time} 預排步驟
 */
function normalize(o) {
  const items = Array.isArray(o.items)
    ? o.items
    : [{ name: o.items || '訂單商品', spec: null, unitPrice: o.total ?? 0, qty: o.picCount || 1 }];

  let timeline = [];
  if (Array.isArray(o.timeline)) {
    timeline = o.timeline.map((t, i, arr) => ({
      state: t.done === undefined
        ? (i === arr.length - 1 ? 'now' : 'done')   // API：歷程，最後一筆是目前狀態
        : (t.done ? 'done' : 'pending'),            // mock：預排步驟
      label: t.label,
      time: t.time || fmtTime(t.createdAt),
    }));
  }

  return {
    id: o.id,
    date: fmtTime(o.createdAt) || o.createdAt || '',
    statusLabel: o.statusLabel || o.status || '',
    items,
    timeline,
    subtotal: o.subtotal ?? 0,
    shipping: o.shipping ?? o.shipFee ?? 0,
    discount: o.discount,                            // mock 才有，API 無折扣
    total: o.total ?? 0,
    payMethod: o.contactMethodLabel ? `專人結帳 · ${o.contactMethodLabel}` : (o.payMethod || '專人結帳'),
    contactInfo: o.contactInfo,
    recipient: o.recipientName || o.recipient || '',
    phone: o.recipientPhone || o.phone || '',
    address: o.recipientAddress || o.address || '',
    shippingMethodLabel: o.shippingMethodLabel,
  };
}

function OrderDetailPage() {
  const { id } = useParams();
  const { data: raw, loading, error } = useApi(() => getOrder(id), [id]);

  if (loading) {
    return <main className="v3-od"><p style={{ padding: 60, textAlign: 'center', color: 'var(--ink-soft)' }}>載入訂單中…</p></main>;
  }
  if (error || !raw) {
    return (
      <main className="v3-od">
        <p style={{ padding: 60, textAlign: 'center' }}>
          找不到此訂單。<Link to="/account">回會員中心</Link>
        </p>
      </main>
    );
  }

  const order = normalize(raw);

  return (
    <main className="v3-od">
      <nav className="v3-od-crumbs">
        <Link to="/account">會員中心</Link> / <span>我的訂單</span> / <b>{order.id}</b>
      </nav>
      <h1>訂單詳情</h1>
      <p className="sub">#{order.id} · {order.date} · <b>{order.statusLabel}</b></p>

      <div className="v3-od-grid">
        <section>
          <div className="v3-od-section">
            <h3>訂單狀態 <span className="pill">{order.statusLabel}</span></h3>
            <div className="v3-tl">
              {order.timeline.map((t, i) => (
                <div key={i} className={`v3-tl-item ${t.state}`}>
                  <span className="dot">{t.state === 'done' && '✓'}</span>
                  <b>{t.label}</b>
                  <span className="time">{t.time}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="v3-od-section">
            <h3>訂購商品 · {order.items.length} 件</h3>
            <div className="v3-od-items">
              {order.items.map((item, i) => (
                <div key={i} className="v3-od-item">
                  <div className="pic">
                    <svg viewBox="0 0 200 600"><use href="#dart-full" /></svg>
                  </div>
                  <div>
                    <b>{item.name}</b>
                    <small>{[item.spec, `數量 ${item.qty}`].filter(Boolean).join(' · ')}</small>
                  </div>
                  <span className="price">NT${((item.unitPrice ?? item.price ?? 0) * (item.qty ?? 1)).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <aside className="v3-od-side">
          <div className="panel">
            <h4>付款明細</h4>
            <div className="row"><span>商品總額</span><b>NT${order.subtotal.toLocaleString()}</b></div>
            <div className="row"><span>運費{order.shippingMethodLabel ? ` · ${order.shippingMethodLabel}` : ''}</span><b>{order.shipping === 0 ? '免運' : `NT$${order.shipping}`}</b></div>
            {order.discount != null && (
              <div className="row"><span>折扣</span><b style={{ color: 'var(--red)' }}>−NT${order.discount}</b></div>
            )}
            <div className="row total"><span>應付</span><b>NT${order.total.toLocaleString()}</b></div>
            <p style={{ font: "600 11px 'Inter', sans-serif", color: 'var(--ink-soft)', marginTop: 8 }}>
              {order.payMethod}{order.contactInfo ? `（${order.contactInfo}）` : ''}
            </p>
          </div>

          <div className="panel">
            <h4>收貨資訊</h4>
            <p className="small">
              <b>{order.recipient}</b>
              {order.phone}<br />
              {String(order.address).split('\n').map((line, i) => <span key={i}>{line}<br /></span>)}
            </p>
          </div>

          <div className="panel">
            <h4>需要協助？</h4>
            <div className="actions">
              <button onClick={() => window.open(LINE_ADD_URL, '_blank', 'noopener,noreferrer')}>聯繫客服 · LINE</button>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default OrderDetailPage;
